import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from "moment";
import { ReduxStateWorkorderitems } from "../../types/redux/reduxStateWorkorderItems";
import { initialStateWorkorderitems } from "../defaultState";
import { getWorkorderItemsByDepartmentAndDate, getWorkorderItemsByWorkorder } from "../../aplApi/workorderItemsHelper";
import { aplSettingsFromState } from "../helpers/aplSettingsHelper";
import { IRootState } from "../../types/redux/IRootStore";
import { WorkorderItem } from "../../types/workorderItem";
import { ActivityType } from "../../types/activityType";
import { assignConsumption, assignLabelling } from "../../aplApi/activitiesHelper";
import { clearClosedLineIdRedux } from "./activity.feature";
import { getAplError } from "../../aplApi/exceptionHelper";

export const getWorkorderItemsRedux = createAsyncThunk("workorderitems/getWorkorderItemsRedux", async (lineId: number, thunkApi) => {
    let state = thunkApi.getState() as IRootState;
    let workorderItems = state.workorderItems;
    let aplSettings = aplSettingsFromState(state);
    let dStartString = moment(workorderItems.dateStartSelectorDate).format("yyyyMMDDHHmm");
    let dEndString = moment(workorderItems.dateEndSelectorDate).format("yyyyMMDDHHmm");

    try {
        if (workorderItems.workorderSearch.length > 0) {
            let response = await getWorkorderItemsByWorkorder(aplSettings.siteId, workorderItems.workorderSearch);
            return response.data;
        } else {
            let response = await getWorkorderItemsByDepartmentAndDate(
                aplSettings.departmentId,
                lineId,
                dStartString,
                dEndString,
                workorderItems.showClosed
            );
            return response.data;
        }
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const assignWorkorderItemRedux = createAsyncThunk(
    "workorderitems/assignActivityRedux",
    async (
        data: {
            workorderItem: WorkorderItem;
            activityType: ActivityType;
        },
        thunkApi
    ) => {
        let state = thunkApi.getState() as IRootState;
        let workorderItems = state.workorderItems;
        let aplSettings = aplSettingsFromState(state);
        let dStartString = moment(workorderItems.dateStartSelectorDate).format("yyyyMMDDHHmm");
        let dEndString = moment(workorderItems.dateEndSelectorDate).format("yyyyMMDDHHmm");

        try {
            if (data.activityType === ActivityType.Consumption) {
                await assignConsumption(data.workorderItem.lineId, data.workorderItem.operationId);
                thunkApi.dispatch(clearClosedLineIdRedux());
            }
            if (data.activityType === ActivityType.Labelling) {
                await assignLabelling(data.workorderItem.lineId, data.workorderItem.operationId);
                thunkApi.dispatch(clearClosedLineIdRedux());
            }
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }

        try {
            if (workorderItems.workorderSearch.length > 0) {
                let response = await getWorkorderItemsByWorkorder(aplSettings.siteId, workorderItems.workorderSearch);
                return response.data;
            } else {
                let response = await getWorkorderItemsByDepartmentAndDate(
                    aplSettings.departmentId,
                    data.workorderItem.lineId,
                    dStartString,
                    dEndString,
                    workorderItems.showClosed
                );
                return response.data;
            }
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

const workorderItems = createSlice({
    name: "workorderItems",
    initialState: initialStateWorkorderitems,
    reducers: {
        onWorkordersPurge: function (state) {
            return { ...state, workorderItem: [] } as ReduxStateWorkorderitems;
        },
        onDtStartChange: function (state, action) {
            return {
                ...state,
                dateStartSelectorVisible: false,
                dateStartSelectorDate: moment(action.payload).toObject()
            } as ReduxStateWorkorderitems;
        },
        onDtEndChange: function (state, action) {
            return {
                ...state,
                dateEndSelectorDate: moment(action.payload).toObject()
            } as ReduxStateWorkorderitems;
        },
        onClearWorkorders: function (state) {
            return {
                ...state,
                workorderItemsLoading: false,
                workorderItemsFirstLoaded: true,
                workorderItemsError: null,
                workorderItems: []
            } as ReduxStateWorkorderitems;
        },
        onSetSelectedWorkorders: function (state, action) {
            return {
                ...state,
                selectedWorkorders: action.payload
            } as ReduxStateWorkorderitems;
        },
        onSetFilters: (state, action): ReduxStateWorkorderitems => ({ ...state, filters: action.payload }),
        onSetSort: (state, action): ReduxStateWorkorderitems => ({ ...state, sort: action.payload }),
        onSetPage: (state, action): ReduxStateWorkorderitems => ({ ...state, page: action.payload }),
        onSetShowClosed: (state, action): ReduxStateWorkorderitems => ({ ...state, showClosed: action.payload }),
        onSetWorkorderSearch: (state, action): ReduxStateWorkorderitems => ({ ...state, workorderSearch: action.payload }),
        onClearAssigned: (state): ReduxStateWorkorderitems => ({...state, assignedCons: null, assignedLabl: null}),
        clearWorkorderManagement: (): ReduxStateWorkorderitems => initialStateWorkorderitems,
        setWorkorderItemsError: (state, action): ReduxStateWorkorderitems => ({ ...state, workorderItemsError: action.payload })
    },
    extraReducers: builder => {
        builder
            .addCase(getWorkorderItemsRedux.pending, state => {
                return {
                    ...state,
                    workorderItemsLoading: true
                } as ReduxStateWorkorderitems;
            })
            .addCase(getWorkorderItemsRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    workorderItemsLoading: false,
                    workorderItemsFirstLoaded: true,
                    workorderItemsError: null,
                    workorderItems: action.payload,
                    assignedCons: null,
                    assignedLabl: null
                } as ReduxStateWorkorderitems;
            })
            .addCase(getWorkorderItemsRedux.rejected, (state, action) => {
                return {
                    ...state,
                    workorderItemsLoading: false,
                    actCons: [],
                    workorderItemsError: action.error.code === "ERR_CANCELED" ? null : action.payload,
                    assignedCons: null,
                    assignedLabl: null
                } as ReduxStateWorkorderitems;
            });

        builder
            .addCase(assignWorkorderItemRedux.pending, state => {
                return {
                    ...state,
                    workorderItemsLoading: true
                } as ReduxStateWorkorderitems;
            })
            .addCase(assignWorkorderItemRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    workorderItemsLoading: false,
                    workorderItemsFirstLoaded: true,
                    workorderItemsError: null,
                    workorderItems: action.payload,
                    assignedCons: action.meta.arg.activityType === ActivityType.Consumption ? action.meta.arg.workorderItem : null,
                    assignedLabl: action.meta.arg.activityType === ActivityType.Labelling ? action.meta.arg.workorderItem : null
                } as ReduxStateWorkorderitems;
            })
            .addCase(assignWorkorderItemRedux.rejected, (state, action) => {
                return {
                    ...state,
                    workorderItemsLoading: false,
                    workorderItemsError: action.error.code === "ERR_CANCELED" ? null : action.payload,
                    assignedCons: null,
                    assignedLabl: null
                } as ReduxStateWorkorderitems;
            });
    }
});

export const {
    onDtStartChange,
    onDtEndChange,
    onClearWorkorders,
    onSetSelectedWorkorders,
    onSetSort,
    onSetFilters,
    onSetPage,
    onSetShowClosed,
    onSetWorkorderSearch,
    onClearAssigned,
    clearWorkorderManagement,
    setWorkorderItemsError
} = workorderItems.actions;
export default workorderItems.reducer;
